// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  type UniverseId,
  type Universe,
} from '@shared/data-access/interfaces/api/engine/citec';
import { exportTableToExcel } from '@shared/ui-kit/utils/export-table-to-excel';
import { type Screener } from '@shared/data-access/interfaces/redux/my-research-state';

export const exportUniversesToExcel = async (
  data: Screener[],
  getUniverse: (params: UniverseId) => Promise<Universe>,
) => {
  const universes = await Promise.allSettled(
    data.map(({ universe_id }) => getUniverse({ universe_id })),
  );

  const excelData = universes
    .filter(
      (universe): universe is PromiseFulfilledResult<Universe> =>
        universe.status === 'fulfilled',
    )
    .map(({ value }) => ({
      universe_id: value.universe_id,
      creation_date: value.creation_date,
      description: value.description,
      esg_exclusion: value.esg_exclusions.join(','),
      to_exclude: value.to_exclude.join(','),
      to_include: value.to_include.join(','),
      sectors_filter: value.sectors_filter.join(','),
      ...Object.fromEntries(
        Array.from(Object.entries(value.esg_scoring), ([key, value]) => [
          `esg_scoring_${key}`,
          Array.isArray(value) ? value.join(',') : value,
        ]),
      ),
      ...Object.fromEntries(
        Array.from(Object.entries(value.factors_filter), ([key, value]) => [
          `factors_filter_${key}`,
          Array.isArray(value) ? value.join(',') : value,
        ]),
      ),
      ...Object.fromEntries(
        Array.from(Object.entries(value.universe_base), ([key, value]) => [
          `universe_base_${key}`,
          Array.isArray(value) ? value.join(',') : value,
        ]),
      ),
    }));

  exportTableToExcel(
    excelData,
    excelData.length > 1 ? 'screeners' : `${excelData[0].description} - screener`,
  );
};
